import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { uiState } from 'react-redux-ui-state';

import AddonCards from './AddonCards/AddonCardsContainer';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  newInstanceService: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  newInstanceFlavorIndex: PropTypes.number.isRequired,
  newInstancePlan: PropTypes.shape({
    nodes: PropTypes.shape({}),
  }).isRequired,
  selectFlavor: PropTypes.func.isRequired,
  selectPlan: PropTypes.func.isRequired,
  flavors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  addons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  plans: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  newInstanceServiceSubtype: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  newInstancePlanIndex: PropTypes.number.isRequired,
  newInstanceAddons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getPrice: PropTypes.func.isRequired,
  regionId: PropTypes.string.isRequired,
  getPriceDebounced: PropTypes.func.isRequired,
  setUiState: PropTypes.func.isRequired,
  priceInitialized: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    organizationId: PropTypes.string,
    ORv4ID: PropTypes.string,
  }).isRequired,
};

export function UnstyledStepTwo(props) {
  const {
    classes,
    newInstanceFlavorIndex,
    newInstancePlan,
    selectFlavor,
    selectPlan,
    flavors,
    plans,
    addons,
    newInstanceServiceSubtype,
    newInstancePlanIndex,
    newInstanceService,
    newInstanceAddons,
    getPrice,
    regionId,
    getPriceDebounced,
    setUiState,
    priceInitialized,
    user,
  } = props;

  const handleWidgetClick = (requestSource) => {
    // init user specs
    const { id, email, organizationId, ORv4ID } = user;
    // init realtime instance spec. selections
    const instanceService = newInstanceService.name || '';
    const requestType = requestSource || '';
    // eslint-disable-next-line no-underscore-dangle
    const chatlioWidget = window._chatlio;
    if (chatlioWidget.isShown()) {
      chatlioWidget.hide();
      chatlioWidget.reset();
    } else {
      // identify the user
      chatlioWidget.identify(id, {
        email,
        organizationId,
        ORv4ID,
        instanceService,
        requestType,
      });
      // configure the widget
      chatlioWidget.configure({
        status: 'offline',
        offlineGreeting: 'Looking for a custom plan size? Let us know!',
        offlineMessagePlaceholder: 'What would you like your plan to look like?',
      });
      chatlioWidget.show({ expanded: true });
    }
  };

  const validFlavors = flavors.filter(
    (catalogFlavor) =>
      catalogFlavor.serviceSubtype === newInstanceServiceSubtype.id &&
      catalogFlavor.displaySetting === 1,
  );

  const plansBySubtype = plans.filter(
    (catalogPlan) => catalogPlan.serviceSubtype === newInstanceServiceSubtype.id,
  );

  const validPlans = validFlavors.reduce((acc, validFlavor) => {
    acc[validFlavor.id] = plansBySubtype.filter(
      (planBySubtype) =>
        planBySubtype.flavor === validFlavor.id && planBySubtype.displaySetting === 1,
    );
    return acc;
  }, {});

  const addonsByServiceSubtype = addons.filter(
    (addon) => addon.serviceSubtype === newInstanceServiceSubtype.id && addon.displaySetting === 1,
  );

  const hasAddons = addonsByServiceSubtype.length > 0;

  if (!priceInitialized) {
    getPrice(
      regionId,
      newInstancePlanIndex,
      validPlans[validFlavors[newInstanceFlavorIndex > 0 ? newInstanceFlavorIndex : 0].id],
      newInstanceAddons,
    );
    setUiState({ priceInitialized: true });
  }

  function panelChanger(index, defaultPlan) {
    selectFlavor(index, defaultPlan);
    setUiState({ priceInitialized: false });
  }

  const flavorPanels = validFlavors.map((validFlavor, index) => (
    <ExpansionPanel
      id={`panel${index}`}
      key={validFlavor.name}
      elevation={0}
      value={index}
      className={classes.flavorPanel}
      // expanded={hasAddons ? newInstanceFlavorIndex === index : true}
      expanded={newInstanceFlavorIndex === index}
      onChange={hasAddons ? () => panelChanger(index, validPlans[validFlavor.id][0]) : null}
      onClick={hasAddons ? null : () => panelChanger(index, validPlans[validFlavor.id][0])}
    >
      <ExpansionPanelSummary
        expandIcon={validFlavors.length === 1 ? null : <ExpandMoreIcon />}
        className={classes.panelSummary}
        classes={{
          content: classes.panelSummaryContent,
          expanded: classes.panelSummaryExpanded,
          expandIcon: classes.panelSumaryExpandIcon,
        }}
        is-selected={newInstanceFlavorIndex === index ? '1' : '0'}
      >
        {validFlavor.name}
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.panelDetail}>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: validFlavor.description }} />
      </ExpansionPanelDetails>
    </ExpansionPanel>
  ));

  const planNode =
    typeof newInstancePlan.nodes !== 'undefined'
      ? newInstancePlan.nodes[newInstanceService.name.toLowerCase()]
      : {};

  function handleButtonClick(newPlanIndex) {
    selectPlan(newPlanIndex, validPlans[validFlavors[newInstanceFlavorIndex].id]);
    getPriceDebounced(
      regionId,
      newPlanIndex,
      validPlans[validFlavors[newInstanceFlavorIndex].id],
      newInstanceAddons,
    );
  }

  let currentPlan;
  if (newInstanceFlavorIndex === -1) {
    selectFlavor(0, validPlans[validFlavors[0].id][0]);
    currentPlan = validPlans[validFlavors[0].id];
  } else {
    currentPlan = validPlans[validFlavors[newInstanceFlavorIndex].id];
  }

  const dbtype = newInstanceService.name.toLowerCase();
  const selectedPlanStorage = (() => {
    if (dbtype === 'redis') {
      return null;
    }
    if (dbtype === 'mongodb') {
      return `${planNode.diskSizeGb}GB`;
    }
    const storageSize =
      newInstanceFlavorIndex > -1 ? planNode.diskSizeGb * planNode.quantity : null;
    return `${storageSize}GB`;
  })();

  const selectedPlanMemory = (() => {
    if (newInstanceFlavorIndex === -1) {
      return null;
    }

    let memorySize;
    if (dbtype === 'redis' || dbtype === 'mongodb') {
      memorySize = planNode.memoryMb / 1024;
    } else {
      memorySize = (planNode.memoryMb / 1024) * planNode.quantity;
    }
    return `${memorySize}GB`;
  })();

  const separator = selectedPlanStorage && selectedPlanMemory ? ' / ' : null;

  return (
    <React.Fragment>
      <div className={classes.planContainer}>
        <div className={classes.cardStuff}>
          <div className={classes.flavorSection} addonspresent={hasAddons ? '1' : '0'}>
            <Typography className={classes.planSectionHeader} component="h5">
              Pick Your Flavor:
            </Typography>
            <div className={classes.cardContainer} addonspresent={hasAddons ? '1' : '0'}>
              {flavorPanels}
            </div>
          </div>

          {hasAddons && (
            <div className={classes.addonSection}>
              <Typography className={classes.planSectionHeader} component="h5">
                Customize Your Instance:
              </Typography>
              <AddonCards
                addons={addonsByServiceSubtype}
                priceInfo={[regionId, newInstancePlanIndex, currentPlan]}
              />
            </div>
          )}
        </div>
        <div className={classes.sliderStuff}>
          <Typography className={classes.planSectionHeader} component="h5">
            Capacity (Storage/Memory):
          </Typography>
          <div className={classes.sliderButtonContainer}>
            <Button
              name="lowerPlanButton"
              disabled={newInstanceFlavorIndex < 0}
              variant="contained"
              color="secondary"
              classes={{ root: classes.removeButtonRoot }}
              onClick={() => handleButtonClick(newInstancePlanIndex - 1)}
            >
              <Icon>remove</Icon>
            </Button>
            <Typography
              component="span"
              color="primary"
              classes={{ root: classes.sizeDisplayRoot }}
            >
              {selectedPlanStorage}
              {separator}
              {selectedPlanMemory}
            </Typography>
            <Button
              name="higherPlanButton"
              disabled={newInstanceFlavorIndex < 0}
              variant="contained"
              color="secondary"
              classes={{ root: classes.addButtonRoot }}
              onClick={() => handleButtonClick(newInstancePlanIndex + 1)}
            >
              <Icon>add</Icon>
            </Button>
          </div>
        </div>
        <div className={classes.widgetContainer}>
          <Typography
            name="requestPlan"
            className={classes.widgetButtonLabel}
            onClick={() => handleWidgetClick('Request Plan')}
          >
            Don&apos;t Like What You See? Request A Custom Plan
          </Typography>
        </div>
      </div>
    </React.Fragment>
  );
}

UnstyledStepTwo.propTypes = propTypes;

const styles = (theme) => ({
  planContainer: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    flexDirection: 'column',
    rowGap: `${theme.spacing(2)}px`,

    [theme.breakpoints.down('md')]: {
      marginBottom: 0,
    },
  },
  planSectionHeader: {
    fontSize: 12,
    marginBottom: '.2rem',
  },
  cardStuff: {
    display: 'flex',
    justifyContent: 'space-around',
    columnGap: theme.spacing(2),
    rowGap: `${theme.spacing(2)}px`,

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  flavorSection: {
    flex: '1',
  },
  addonSection: {
    flex: '1',
  },
  cardContainer: {
    '&[addonspresent="0"]': {
      display: 'flex',
      alignItems: 'baseline',
      columnGap: theme.spacing(2),

      [theme.breakpoints.down('md')]: {
        alignItems: 'stretch',
      },
    },

    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  flavorPanel: {
    flex: 1,
    border: `1px solid ${theme.customColors.black}`,
    borderRadius: 4,
    overflow: 'hidden',
    '&&': { margin: `0 0 ${theme.spacing(1)}px 0` },

    '&:hover': {
      cursor: 'pointer',
    },
  },
  panelSummary: {
    '&&': { minHeight: 'unset' },
    display: 'block',
    position: 'relative',

    '&[is-selected="1"]': {
      backgroundColor: theme.customColors.sky,
    },
  },
  panelSummaryContent: {
    justifyContent: 'center',
    fontWeight: 'bold',
    '&&': { margin: 0 },

    '&[expanded]': {
      margin: 0,
    },
  },
  panelSumaryExpandIcon: {
    margin: 0,
    padding: 0,
    position: 'absolute',
    top: 0,
    right: 0,
  },
  panelDetail: {
    justifyContent: 'center',
    padding: theme.spacing(2),

    '& ul': {
      margin: 0,
      padding: 0,
      fontSize: '0.8571rem',
      color: theme.customColors.mediumGrey,
      listStyleType: 'none',
      textAlign: 'center',
      lineHeight: '1.2857rem',
    },
    '& h6': { display: 'none' },
  },
  planHeader: {
    fontWeight: 'bold',
  },
  sliderStuff: {
    flexGrow: '1',
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
    marginTop: '24px',
    justifyContent: 'space-between',
    '&:last-child': {
      marginBottom: '1rem',
    },

    [theme.breakpoints.down('md')]: {
      padding: `0 ${theme.spacing(1)}px`,
    },
  },
  sliderRoot: {
    marginTop: '.5rem',
  },
  sliderTrack: {
    backgroundColor: theme.customColors.teal,
  },
  sliderButtonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
    },
  },
  sizeDisplayRoot: {
    fontSize: '2.5rem',
    border: `1px solid ${theme.customColors.mediumGrey}`,
    borderRadius: 4,
    width: '100%',
    textAlign: 'center',

    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
    },
  },
  removeButtonRoot: {
    borderRadius: '50% 0 0 50%',
    minWidth: 'unset',
    width: theme.spacing(6),
    height: theme.spacing(6),
    marginRight: theme.spacing(1),
  },
  addButtonRoot: {
    borderRadius: '0 50% 50% 0',
    minWidth: 'unset',
    width: theme.spacing(6),
    height: theme.spacing(6),
    marginLeft: theme.spacing(1),
  },
  nodeLabel: {
    ...theme.typography.h5,
    textAlign: 'center',
    marginTop: theme.spacing(1),
    fontSize: '12px',

    [theme.breakpoints.down('md')]: {
      textAlign: 'left',
      marginTop: theme.spacing(2),
    },
  },
  nodesAndInfo: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
  },
  nodeGrid: {
    justifyContent: 'center',

    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
      width: 'unset',
    },
  },
  nodeRoot: {
    boxShadow:
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    fontSize: '1.15rem',
    margin: '0 10px',

    [theme.breakpoints.down('md')]: {
      marginRight: '.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      marginRight: 'unset',
    },
  },
  primaryNode: {
    backgroundColor: theme.customColors.black,
  },
  secondaryNode: {
    color: theme.customColors.black,
    backgroundColor: theme.customColors.lightGrey,
  },
  sizeInfo: {
    textAlign: 'center',
    color: theme.customColors.orange,
    fontSize: '1.25rem',
    marginTop: theme.spacing(3),
  },
  contactSupportText: {
    fontSize: '.8rem',
    borderRadius: 'unset',
    textAlign: 'center',
    fontStyle: 'italic',
    color: theme.customColors.mediumGrey,
    paddingTop: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  rocketContainer: {
    marginTop: '1rem',
  },
  widgetButtonLabel: {
    color: '#0000EE',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontStyle: 'italic',
    fontSize: '11px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    width: 'max-content',
  },
  widgetContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  sliderComponent: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
});

export const uiConfig = {
  name: 'createInstance',
  state: () => ({
    priceInitialized: false,
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledStepTwo));
